import React, { useCallback } from "react";
import { map } from "ramda";
import Button from "@material-ui/core/Button";
import AddIcon from "@material-ui/icons/Add";
import { authTypes } from "./authenticationService";
import Paper from "@material-ui/core/Paper";
import Popper from "@material-ui/core/Popper";
import MenuList from "@material-ui/core/MenuList";
import MenuItem from "@material-ui/core/MenuItem";
import ClickAwayListener from "@material-ui/core/ClickAwayListener";
import Grow from "@material-ui/core/Grow";
import { Box } from "@material-ui/core";
import PropTypes from "prop-types";
import Tooltip from "@material-ui/core/Tooltip";
import IconButton from "@material-ui/core/IconButton";

const AuthenticationListButton = ({
  title,
  onAuthenticationSelected,
  variant = "icon",
}) => {
  const anchorRef = React.useRef(null);
  const [authenticationMenuOpen, setAuthenticationMenuOpen] = React.useState(
    false,
  );

  // return focus to the button when we transitioned from !open -> open
  const prevOpen = React.useRef(authenticationMenuOpen);
  React.useEffect(() => {
    if (prevOpen.current === true && authenticationMenuOpen === false) {
      anchorRef.current.focus();
    }

    prevOpen.current = authenticationMenuOpen;
  }, [authenticationMenuOpen]);

  const handleListKeyDown = useCallback(event => {
    if (event.key === "Tab") {
      event.preventDefault();
      setAuthenticationMenuOpen(false);
    }
  }, []);

  const handleNewAuthenticationClicked = useCallback(() => {
    setAuthenticationMenuOpen(prevOpen => !prevOpen);
  }, []);

  const handleAuthenticationMenuClose = useCallback(event => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }

    setAuthenticationMenuOpen(false);
  }, []);

  const handleAuthenticationMenuSelect = useCallback(
    value => {
      onAuthenticationSelected(value);
      setAuthenticationMenuOpen(false);
    },
    [onAuthenticationSelected],
  );

  return (
    <Box>
      {variant === "button" ? (
        <Button
          ref={anchorRef}
          variant={"text"}
          startIcon={<AddIcon />}
          aria-controls={authenticationMenuOpen ? "menu-list-grow" : undefined}
          aria-haspopup="true"
          onClick={handleNewAuthenticationClicked}
        >
          {title}
        </Button>
      ) : (
        <Tooltip title={title}>
          <IconButton
            ref={anchorRef}
            aria-controls={
              authenticationMenuOpen ? "menu-list-grow" : undefined
            }
            aria-haspopup="true"
            onClick={handleNewAuthenticationClicked}
          >
            <AddIcon color="primary" fontSize="large" />
          </IconButton>
        </Tooltip>
      )}
      <Popper
        open={authenticationMenuOpen}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
        placement="bottom"
        style={{ zIndex: 100 }}
      >
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin:
                placement === "bottom" ? "center top" : "center bottom",
            }}
          >
            <Paper variant={"outlined"}>
              <ClickAwayListener onClickAway={handleAuthenticationMenuClose}>
                <MenuList
                  autoFocusItem={authenticationMenuOpen}
                  id="menu-list-grow"
                  onKeyDown={handleListKeyDown}
                >
                  {map(
                    at => (
                      <MenuItem
                        key={at.value}
                        onClick={() => handleAuthenticationMenuSelect(at.value)}
                      >
                        {at.display}
                      </MenuItem>
                    ),
                    authTypes,
                  )}
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </Box>
  );
};

AuthenticationListButton.propTypes = {
  title: PropTypes.string,
  onAuthenticationSelected: PropTypes.func,
  variant: PropTypes.oneOf(["button", "icon"]),
};

export default AuthenticationListButton;
